import type { BookmakerOdds, OddsData } from "./types";
import type { Labels } from "@/lib/labels/types";

type OddsLabels = Labels["matchPage"]["odds"];

/**
 * Mapping from API selection names to translation keys for each market type
 */
const SELECTION_LABEL_MAP: Record<
    string,
    Record<
        string,
        | keyof OddsLabels["selectionsDoubleChance"]
        | keyof OddsLabels["selectionsBtts"]
        | keyof OddsLabels["selectionsOverUnder"]
    >
> = {
    // Double Chance
    DOUBLE_CHANCE: {
        "1x": "homeOrDraw",
        "12": "homeOrAway",
        x2: "drawOrAway",
    },
    // Both Teams to Score
    BOTH_TO_SCORE: {
        yes: "yes",
        no: "no",
    },
    // Over/Under - handled specially for capitalization
    OVER_UNDER: {
        over: "over",
        under: "under",
    },
};

/**
 * Optional team context for HT/FT labels to display team names instead of "1/1".
 */
export interface HtftTeamContext {
    homeTeamName: string;
    awayTeamName: string;
    drawLabel: string;
}

/**
 * Translates odds selection labels using the localization system.
 * Handles:
 * - Double Chance: 1x → 1X, 12 → 12, x2 → X2
 * - HT/FT: home/home → "Leeds/Leeds", etc. (when teamContext provided)
 * - Over/Under: over → Over, under → Under (capitalized)
 *
 * @param bookmakerOdds - Array of bookmaker odds to translate
 * @param marketType - The market type (e.g., "DOUBLE_CHANCE", "OVER_UNDER")
 * @param oddsLabels - The odds labels from the localization system
 * @param teamContext - Optional team names for HT/FT labels
 * @returns Array of bookmaker odds with translated labels
 */
export function translateOddsSelections(
    bookmakerOdds: BookmakerOdds[],
    marketType: string,
    oddsLabels: OddsLabels,
    teamContext?: HtftTeamContext
): BookmakerOdds[] {
    return bookmakerOdds.map((bookie) => ({
        ...bookie,
        odds: bookie.odds.map((odd) => translateOddLabel(odd, marketType, oddsLabels, teamContext)),
    }));
}

/**
 * Translates a single odds label
 */
function translateOddLabel(
    odd: OddsData,
    marketType: string,
    oddsLabels: OddsLabels,
    teamContext?: HtftTeamContext
): OddsData {
    const originalLabel = odd.label.toLowerCase();

    // Handle plain 1X2 (both FT and HT). Selections come through as lowercase
    // codes ("1" / "x" / "2"); we uppercase / translate to the tenant locale
    // so buttons read "1" / "X" / "2" (or the locale's home/draw/away glyphs).
    if (marketType === "FT_1X2" || marketType === "HT_1X2" || marketType === "1X2") {
        const key: keyof OddsLabels["selections1x2"] | null =
            originalLabel === "1" ? "home" : originalLabel === "x" ? "draw" : originalLabel === "2" ? "away" : null;
        if (key && oddsLabels.selections1x2[key]) {
            return { ...odd, label: oddsLabels.selections1x2[key] };
        }
    }

    // Handle Double Chance
    if (marketType === "DOUBLE_CHANCE") {
        const mapping = SELECTION_LABEL_MAP.DOUBLE_CHANCE;
        const key = mapping[originalLabel] as keyof OddsLabels["selectionsDoubleChance"] | undefined;
        if (key && oddsLabels.selectionsDoubleChance[key]) {
            return { ...odd, label: oddsLabels.selectionsDoubleChance[key] };
        }
    }

    // Handle Both Teams to Score. Accept both the legacy `BOTH_TO_SCORE`
    // canonical key and the raw API code `BOTH_TEAMS_SCORE`.
    if (marketType === "BOTH_TO_SCORE" || marketType === "BOTH_TEAMS_SCORE") {
        const mapping = SELECTION_LABEL_MAP.BOTH_TO_SCORE;
        const key = mapping[originalLabel] as keyof OddsLabels["selectionsBtts"] | undefined;
        if (key && oddsLabels.selectionsBtts[key]) {
            return { ...odd, label: oddsLabels.selectionsBtts[key] };
        }
    }

    // Handle Over/Under — translate just the direction ("Over" / "Under").
    // The threshold belongs in the card title, not on the odds button, so we
    // deliberately drop any trailing number the operator sent us.
    if (marketType === "OVER_UNDER") {
        const match = originalLabel.match(/^(over|under)\b/i);
        if (match) {
            const key = match[1].toLowerCase() as keyof OddsLabels["selectionsOverUnder"];
            const translated = oddsLabels.selectionsOverUnder[key];
            if (translated) return { ...odd, label: translated };
        }
    }

    // Handle HT/FT - translate to team names (e.g., "Leeds/Leeds") or fall back to "1/1"
    if (marketType === "FIRST_HALF_AND_FINAL_RESULT") {
        // Map raw API labels to semantic keys: "home", "draw", "away" for each half
        type HtftSemantic = { ht: "home" | "draw" | "away"; ft: "home" | "draw" | "away" };
        const htftSemanticMapping: Record<string, HtftSemantic> = {
            "home/home": { ht: "home", ft: "home" },
            "home/draw": { ht: "home", ft: "draw" },
            "home/away": { ht: "home", ft: "away" },
            "draw/home": { ht: "draw", ft: "home" },
            "draw/draw": { ht: "draw", ft: "draw" },
            "draw/away": { ht: "draw", ft: "away" },
            "away/home": { ht: "away", ft: "home" },
            "away/draw": { ht: "away", ft: "draw" },
            "away/away": { ht: "away", ft: "away" },
            "1/1": { ht: "home", ft: "home" },
            "1/x": { ht: "home", ft: "draw" },
            "1/2": { ht: "home", ft: "away" },
            "x/1": { ht: "draw", ft: "home" },
            "x/x": { ht: "draw", ft: "draw" },
            "x/2": { ht: "draw", ft: "away" },
            "2/1": { ht: "away", ft: "home" },
            "2/x": { ht: "away", ft: "draw" },
            "2/2": { ht: "away", ft: "away" },
            "11": { ht: "home", ft: "home" },
            "1x": { ht: "home", ft: "draw" },
            "12": { ht: "home", ft: "away" },
            x1: { ht: "draw", ft: "home" },
            xx: { ht: "draw", ft: "draw" },
            x2: { ht: "draw", ft: "away" },
            "21": { ht: "away", ft: "home" },
            "2x": { ht: "away", ft: "draw" },
            "22": { ht: "away", ft: "away" },
        };

        const semantic = htftSemanticMapping[originalLabel];
        if (semantic) {
            // If team names are provided, use them for the label
            if (teamContext) {
                const nameMap = {
                    home: teamContext.homeTeamName,
                    draw: teamContext.drawLabel,
                    away: teamContext.awayTeamName,
                };
                return { ...odd, label: `${nameMap[semantic.ht]}/${nameMap[semantic.ft]}` };
            }

            // Fall back to locale-based "1/1" style labels
            const htftKeyMapping: Record<string, keyof OddsLabels["selectionsHtft"]> = {
                "home-home": "homeHome",
                "home-draw": "homeDraw",
                "home-away": "homeAway",
                "draw-home": "drawHome",
                "draw-draw": "drawDraw",
                "draw-away": "drawAway",
                "away-home": "awayHome",
                "away-draw": "awayDraw",
                "away-away": "awayAway",
            };
            const localeKey = htftKeyMapping[`${semantic.ht}-${semantic.ft}`];
            if (localeKey && oddsLabels.selectionsHtft[localeKey]) {
                return { ...odd, label: oddsLabels.selectionsHtft[localeKey] };
            }
        }
    }

    return odd;
}
